import {
  Calendar,
  Plus,
  Filter,
  ChevronDown,
  Check,
  Trophy,
  ChevronRight,
} from 'lucide-react';
import { useRaces, useCreateRace } from '../hooks/useProfile';
import { dayjs } from '../lib/utils';
import RaceModal, { RaceData } from '../components/Modals/RaceModal';
import { Button } from '../components/ui/button';
import Loader from './Loader';
import { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import PageHeader from '../components/PageHeader';

export default function Races() {
  const { data: races, isLoading, error } = useRaces();
  const navigate = useNavigate();
  const { createRace } = useCreateRace();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter states
  const [selectedDistances, setSelectedDistances] = useState<string[]>([]);
  const [selectedTimeFilters, setSelectedTimeFilters] = useState<string[]>([]);
  const [isDistanceDropdownOpen, setIsDistanceDropdownOpen] = useState(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);

  // Available distance options
  const distanceOptions = [
    '5k',
    '10k',
    'half marathon',
    'marathon',
    'ultra marathon',
  ];

  // Available time filter options (removed 'all' since it's multiselect)
  const timeFilterOptions = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'past', label: 'Past' },
    { value: 'this_month', label: 'This Month' },
    { value: 'next_30_days', label: 'Next 30 Days' },
  ];

  const handleCreateRace = async (raceData: RaceData) => {
    setIsSubmitting(true);
    try {
      await createRace({
        name: raceData.name,
        date: raceData.date,
        distance: raceData.distance,
        url: raceData.url,
        description: raceData.description,
        gpx_file_url: raceData.gpx_file_url,
        gpx_file: raceData.gpx_file,
      });

      // Refresh the races list
      await queryClient.invalidateQueries({ queryKey: ['races'] });

      // Show success feedback
      const event = new CustomEvent('showToast', {
        detail: { message: 'Race created successfully!', type: 'success' },
      });
      window.dispatchEvent(event);

      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Failed to create race:', error);
      const event = new CustomEvent('showToast', {
        detail: {
          message: error.message || 'Failed to create race',
          type: 'error',
        },
      });
      window.dispatchEvent(event);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter handlers
  const toggleDistanceFilter = (distance: string) => {
    setSelectedDistances((prev) =>
      prev.includes(distance)
        ? prev.filter((d) => d !== distance)
        : [...prev, distance],
    );
  };

  const toggleTimeFilter = (timeFilter: string) => {
    setSelectedTimeFilters((prev) =>
      prev.includes(timeFilter)
        ? prev.filter((t) => t !== timeFilter)
        : [...prev, timeFilter],
    );
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.relative')) {
        setIsDistanceDropdownOpen(false);
        setIsTimeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered races
  const filteredRaces = useMemo(() => {
    if (!races) return [];

    const uniqueRaces = races.filter(
      (race: RaceData, index: number, allRaces: RaceData[]) =>
        allRaces.findIndex(
          (candidate) =>
            candidate.name.trim().toLowerCase() ===
              race.name.trim().toLowerCase() &&
            dayjs(candidate.date).format('YYYY-MM-DD') ===
              dayjs(race.date).format('YYYY-MM-DD') &&
            String(candidate.distance).trim().toLowerCase() ===
              String(race.distance).trim().toLowerCase(),
        ) === index,
    );

    return uniqueRaces.filter((race: RaceData) => {
      // Distance filter
      if (
        selectedDistances.length > 0 &&
        !selectedDistances.includes(race.distance)
      ) {
        return false;
      }

      // Time filter - if no time filters selected, show all
      if (selectedTimeFilters.length === 0) {
        return true;
      }

      // Check if race matches any of the selected time filters
      const raceDate = dayjs(race.date);
      const now = dayjs();

      return selectedTimeFilters.some((filter) => {
        switch (filter) {
          case 'upcoming':
            return raceDate.isAfter(now);
          case 'past':
            return raceDate.isBefore(now);
          case 'this_month':
            return (
              raceDate.isSame(now, 'month') && raceDate.isSame(now, 'year')
            );
          case 'next_30_days':
            return (
              raceDate.isAfter(now) && raceDate.isBefore(now.add(30, 'days'))
            );
          default:
            return false;
        }
      });
    });
  }, [races, selectedDistances, selectedTimeFilters]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div className="mx-auto h-full w-full max-w-5xl">
      {/* Header with Add Race button */}
      <PageHeader
        eyebrow="Race calendar"
        title="Races"
        description="Keep every target race in view and let your plan work backwards from the start line."
        icon={Trophy}
        action={
          <Button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 rounded-full"
          >
            <Plus className="h-4 w-4" />
            Add race
          </Button>
        }
      />
      <div className="mb-6 flex flex-col gap-4">
        {/* Filters */}
        {races && races.length > 0 ? (
          <div className="flex flex-col gap-3">
            {/* Filter header */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters</span>
            </div>

            {/* Filter dropdowns */}
            <div className="flex flex-wrap gap-4">
              {/* Distance dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setIsDistanceDropdownOpen(!isDistanceDropdownOpen);
                    setIsTimeDropdownOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-brand-offWhite focus:outline-none focus:ring-2 focus:ring-brand-volt"
                >
                  Distance
                  {selectedDistances.length > 0 ? (
                    <span className="bg-brand-volt text-brand-midnight text-xs px-2 py-0.5 rounded-full">
                      {selectedDistances.length}
                    </span>
                  ) : null}
                  <ChevronDown className="h-4 w-4" />
                </button>

                {isDistanceDropdownOpen ? (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-10">
                    <div className="py-1">
                      {distanceOptions.map((distance) => (
                        <button
                          key={distance}
                          onClick={() => toggleDistanceFilter(distance)}
                          className="flex items-center justify-between w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <span>{distance.toUpperCase()}</span>
                          {selectedDistances.includes(distance) ? (
                            <Check className="h-4 w-4 text-brand-cobalt" />
                          ) : null}
                        </button>
                      ))}
                      {selectedDistances.length > 0 ? (
                        <>
                          <div className="border-t border-gray-200 my-1"></div>
                          <button
                            onClick={() => setSelectedDistances([])}
                            className="w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
                          >
                            Clear Distance Filters
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Time period dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setIsTimeDropdownOpen(!isTimeDropdownOpen);
                    setIsDistanceDropdownOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-brand-offWhite focus:outline-none focus:ring-2 focus:ring-brand-volt"
                >
                  Time Period
                  {selectedTimeFilters.length > 0 ? (
                    <span className="bg-brand-green text-brand-midnight text-xs px-2 py-0.5 rounded-full">
                      {selectedTimeFilters.length}
                    </span>
                  ) : null}
                  <ChevronDown className="h-4 w-4" />
                </button>

                {isTimeDropdownOpen ? (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-10">
                    <div className="py-1">
                      {timeFilterOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => toggleTimeFilter(option.value)}
                          className="flex items-center justify-between w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <span>{option.label}</span>
                          {selectedTimeFilters.includes(option.value) ? (
                            <Check className="h-4 w-4 text-brand-green" />
                          ) : null}
                        </button>
                      ))}
                      {selectedTimeFilters.length > 0 ? (
                        <>
                          <div className="border-t border-gray-200 my-1"></div>
                          <button
                            onClick={() => setSelectedTimeFilters([])}
                            className="w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
                          >
                            Clear Time Filters
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Clear all button */}
              {selectedDistances.length > 0 ||
              selectedTimeFilters.length > 0 ? (
                <button
                  onClick={() => {
                    setSelectedDistances([]);
                    setSelectedTimeFilters([]);
                  }}
                  className="px-3 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-md hover:bg-red-200"
                >
                  Clear All
                </button>
              ) : null}
            </div>

            {/* Active filters summary */}
            {selectedDistances.length > 0 || selectedTimeFilters.length > 0 ? (
              <div className="text-sm text-gray-600">
                Showing {filteredRaces.length} of {races.length} races
                {selectedDistances.length > 0 ? (
                  <span> • Distance: {selectedDistances.join(', ')}</span>
                ) : null}
                {selectedTimeFilters.length > 0 ? (
                  <span>
                    {' '}
                    • Time:{' '}
                    {selectedTimeFilters
                      .map(
                        (f) =>
                          timeFilterOptions.find((o) => o.value === f)?.label,
                      )
                      .join(', ')}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {races && races.length > 0 ? (
        filteredRaces.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredRaces.map((race: RaceData) => (
              <Link
                key={race.id}
                to="/races/$id"
                params={{ id: String(race.id) }}
                className="surface-card group flex cursor-pointer flex-col p-5 transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold tracking-tight text-brand-midnight">
                    {race.name}
                  </h2>
                  <ChevronRight className="h-5 w-5 shrink-0 text-brand-cobalt transition-transform group-hover:translate-x-0.5" />
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <p className="text-sm font-medium text-gray-600">
                    {dayjs(race.date).format('MM/DD/YYYY')} (
                    {dayjs(race.date).diff(dayjs(), 'days')} days)
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-2 mb-2">
                  <div className="rounded-full bg-brand-blueSoft px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand-cobalt">
                    {race.distance.toUpperCase()}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="surface-card flex h-64 flex-col items-center justify-center rounded-2xl p-6 text-center">
            <p className="mb-4 text-gray-500">
              No races match your current filters
            </p>
            <Button
              onClick={() => {
                setSelectedDistances([]);
                setSelectedTimeFilters([]);
              }}
              variant="outline"
            >
              Clear All Filters
            </Button>
          </div>
        )
      ) : (
        <div className="surface-card flex min-h-64 flex-col items-center justify-center rounded-2xl p-6 text-center">
          <p className="mb-1 text-lg font-bold text-brand-midnight">
            No races yet
          </p>
          <p className="mb-4 text-sm text-gray-500">
            Add your next start line to make your training plan actionable.
          </p>
          <Button onClick={() => setIsModalOpen(true)}>Add Race</Button>
        </div>
      )}

      {/* Race Modal */}
      <RaceModal
        show={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onSubmit={handleCreateRace}
        disabled={false}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
